import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { isAssocie } from '../lib/permissions';

/**
 * KeyboardShortcuts — raccourcis « g puis lettre » pour naviguer, et « ? » pour
 * afficher l'aide. N'interfère jamais avec la saisie (ignoré dans les champs).
 * Monté une fois dans AppLayout. La palette ⌘K vit dans CommandPalette.
 */
const NAV = [
  { k: 'h', label: 'Tableau de bord', path: '/' },
  { k: 'd', label: 'Dossiers', path: '/dossiers' },
  { k: 'c', label: 'Clients', path: '/clients' },
  { k: 'e', label: 'Échéances', path: '/echeances' },
  { k: 't', label: 'Tâches', path: '/taches' },
  { k: 'a', label: 'Calendrier', path: '/calendrier' },
  { k: 's', label: 'Suivi du temps', path: '/temps' },
  { k: 'f', label: 'Facturation', path: '/facturation', mgmt: true },
  { k: 'r', label: 'Rapports', path: '/rapports', mgmt: true },
  { k: 'i', label: 'Maître Adalia', path: '/chat' },
];
const MGMT_KEYS = new Set(NAV.filter((n) => n.mgmt).map((n) => n.k));   // finance : associés
const NAV_MAP = NAV.reduce((acc, n) => { acc[n.k] = n.path; return acc; }, {});

const isTyping = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

export default function KeyboardShortcuts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const assoc = isAssocie(user?.role);
  const assocRef = useRef(assoc);
  assocRef.current = assoc;   // pour le listener clavier (évite une closure périmée)
  const [showHelp, setShowHelp] = useState(false);
  const [armed, setArmed] = useState(false); // « g » pressé, en attente de la 2e touche
  const armedTimer = useRef(null);

  const disarm = useCallback(() => {
    setArmed(false);
    if (armedTimer.current) { clearTimeout(armedTimer.current); armedTimer.current = null; }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;      // ⌘K & co. gérés ailleurs
      if (isTyping(e.target)) return;                       // ne jamais voler la saisie

      // Aide
      if (e.key === '?' || (e.shiftKey && e.key === '/')) { e.preventDefault(); setShowHelp((v) => !v); return; }
      if (e.key === 'Escape') { setShowHelp(false); disarm(); return; }

      // Séquence « g puis lettre »
      if (armed) {
        const key = e.key.toLowerCase();
        const path = NAV_MAP[key];
        disarm();
        // Les raccourcis finance (f/r) sont réservés aux associés.
        if (path && (!MGMT_KEYS.has(key) || assocRef.current)) { e.preventDefault(); navigate(path); }
        return;
      }
      if (e.key === 'g' || e.key === 'G') {
        e.preventDefault();
        setArmed(true);
        armedTimer.current = setTimeout(() => setArmed(false), 1200);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); if (armedTimer.current) clearTimeout(armedTimer.current); };
  }, [armed, navigate, disarm]);

  return (
    <>
      {/* Indicateur discret quand « g » est armé */}
      <AnimatePresence>
        {armed && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.15 }}
            className="fixed bottom-5 left-5 z-[180] flex items-center gap-2 px-3 py-1.5 rounded-[8px] text-[11px]"
            style={{ background: 'var(--widget-bg)', border: '1px solid var(--border-soft)', color: 'var(--text-secondary)' }}
          >
            <kbd className="px-1.5 py-0.5 rounded text-[10px]" style={{ border: '1px solid var(--border-color)', color: '#C9A227' }}>g</kbd>
            <span style={{ color: 'var(--text-tertiary)' }}>puis une lettre… (? pour l'aide)</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Aide */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            className="fixed inset-0 z-[200] flex items-center justify-center px-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
            onMouseDown={() => setShowHelp(false)}
          >
            <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }} />
            <motion.div
              role="dialog" aria-label="Raccourcis clavier"
              className="relative w-full max-w-[520px] rounded-[14px] overflow-hidden"
              style={{ background: 'var(--widget-bg)', border: '1px solid var(--border-soft)', boxShadow: '0 24px 70px rgba(0,0,0,0.45)' }}
              initial={{ opacity: 0, y: -8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.18, ease: [0.22, 0.61, 0.36, 1] }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-3.5 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-color)' }}>
                <span className="font-serif" style={{ fontSize: 16, color: 'var(--text-primary)' }}>Raccourcis clavier</span>
                <kbd className="text-[10px] px-1.5 py-0.5 rounded" style={{ color: 'var(--text-tertiary)', border: '1px solid var(--border-color)' }}>Échap</kbd>
              </div>
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--text-tertiary)' }}>Général</div>
                  <Row keys={['⌘', 'K']} label="Palette de commandes" />
                  <Row keys={['?']} label="Cette aide" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--text-tertiary)' }}>Aller à <span className="normal-case tracking-normal">(g puis…)</span></div>
                  {NAV.filter((n) => !n.mgmt || assoc).map((n) => <Row key={n.k} keys={['g', n.k]} label={n.label} />)}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Row({ keys, label }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span className="flex items-center gap-1">
        {keys.map((k, i) => (
          <kbd key={i} className="min-w-[20px] text-center text-[10.5px] px-1.5 py-0.5 rounded" style={{ border: '1px solid var(--border-color)', color: 'var(--text-tertiary)' }}>{k}</kbd>
        ))}
      </span>
    </div>
  );
}
